import {JoinDisplayInputDetailPaymentMethodForProps, JoinDisplayPaymentMethodsProps} from '../../Join/Components/JoinDisplayPaymentMethodsRadioList'
import {CreditCardInfo, DisplayCreditCardExpireDate, MobileCarrierInfo, MyCreditCardInfo, PaymentInputDetail, SecurityCodeForMyCreditCard, UsingMyCreditCardInfo} from '../../../../Props/ContractInputInfo'
import {findSecurityCodeByCardId, isCheckedMyCardId, isExpiredCreditCard, SelectCreditCardRadioList, SelectCreditCardRadioListForProps} from '../../../Components/PaymentsRelations/InputContentBlocks/SelectCreditCardRadioList'
import React, {useCallback} from 'react'
import {Nullable, Optional} from '../../../../../Common/TypeHelper'
import {Spacer} from '../../../../../Common/Components/Spacer'
import {PIMStack} from '../../../Atoms/Layouts/Bases/PIMStack'
import {MBContentBodyDivider} from '../../../Atoms/Dividers'
import {ErrorDisplay} from '../../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {MobileCarrierInputComponent} from '../../../Components/PaymentsRelations/InputContentBlocks/MobileCarrierInputComponent'
import {MBMemberRadio} from '../../../Atoms/MBMemberRadio'
import {ExpiredCardBadge, MBInUseBadge} from '../../../Atoms/Badges'
import {MBContentBodyItemWarningMessageTypography} from '../../../Atoms/Typographies'
import {CardInformationDiv} from '../../../Components/PaymentsRelations/InputContentBlocks/CardInformationDiv'
import {SecurityCodeInputForm} from '../../../Components/PaymentsRelations/InputContentBlocks/NewCreditCardInputComponent'
import {PAYMENT} from '../../../../../Common/Constants'
import {ContractInfo, NextContractInfo} from '../../../../DTO/MemberContractInfoDTO'
import dayjs from 'dayjs'

export type EditDisplayPaymentMethodsProps = JoinDisplayPaymentMethodsProps & {
  displayMyCreditCardExcludeCurrentCard: boolean
  isChangeContractTerm: boolean
  creditCardInfoForNextContract: Nullable<MyCreditCardInfo>
  nextContractInfoBeforeChange: Pick<NextContractInfo, 'contractMethod'>
  nowContractInfo: Pick<ContractInfo, 'term' | 'endDate'>
} & Required<Pick<SelectCreditCardRadioListForProps, 'nextContractStartDate_YYYYMMDD'>>

export const EditDisplayPaymentMethodsRadioList: React.VFC<EditDisplayPaymentMethodsProps> = (
  {
    disabled,
    paymentMethodList,
    paymentInputDetailWithMethod,
    myCreditCardInfoList,
    errorMessages,
    onChangeSelectedMethod,
    onChangeInputDetailInfo,
    onDeleteCardButtonClick,
    displayMyCreditCardExcludeCurrentCard,
    isChangeContractTerm,
    creditCardInfoForNextContract,
    nextContractStartDate_YYYYMMDD,
    nextContractInfoBeforeChange,
    nowContractInfo,
  },
) => {
  return (
    <PIMStack spacing={'18px'} divider={<MBContentBodyDivider/>} showFirstDivider showEndDivider>
      {paymentMethodList.map((paymentMethod, idx) => {
        const checked = paymentInputDetailWithMethod.method === paymentMethod

        const {contractMethod} = nextContractInfoBeforeChange

        const disabledForCsv = isChangeContractTerm
          && paymentMethod === contractMethod.method
          && contractMethod.method === 'convenience_store'

        const disabledForCarrier = nowContractInfo.term === 'yearly'
          && paymentMethod === 'mobile_carrier'
          && isLastMonthInContractTerm(nowContractInfo.endDate)

        return (
          <div key={`payment-method-${idx}`}>
            <MBMemberRadio
              id={`payment-${idx}`}
              name="payment"
              labelName={PAYMENT.METHODS_FOR_RADIO_LABEL[paymentMethod]}
              disabled={disabled || disabledForCsv || disabledForCarrier}
              hasError={errorMessages.paymentInputMethodErrors.length > 0}
              onChange={(): void => {
                onChangeSelectedMethod(paymentMethod)
              }}
              checked={checked}
              isBoldLabel
            />

            {disabled || disabledForCsv && (
              <div data-testid="csv-notice-message">
                <Spacer mt={'10px'} ml={'5%'}>
                  <MBContentBodyItemWarningMessageTypography>
                    {`既にコンビニ決済となっています。\nお支払いコンビニの選択及びお支払い番号の払い出しは${contractMethod.paymentMethodDetail.paymentAvailableOn}から実施できます。`}
                  </MBContentBodyItemWarningMessageTypography>
                </Spacer>
              </div>
            )}
            {disabled || disabledForCarrier && (
              <div data-testid="carrier-notice-message">
                <Spacer mt={'10px'} ml={'5%'}>
                  <MBContentBodyItemWarningMessageTypography>
                    {`キャリア決済への変更は現在の契約の最終月のみ実施できます。\n ${getChangeTermForCarrier(nowContractInfo.endDate)}`}
                  </MBContentBodyItemWarningMessageTypography>
                </Spacer>
              </div>
            )}

            <EditDisplayInputDetailPaymentMethod
              paymentMethod={paymentMethod}
              myCreditCardInfoList={myCreditCardInfoList}
              paymentInputDetailWithMethod={paymentInputDetailWithMethod}
              errorMessages={errorMessages}
              onChangeInputDetailInfo={
                (value: PaymentInputDetail): void => onChangeInputDetailInfo(value)
              }
              onDeleteCardButtonClick={onDeleteCardButtonClick}
              displayMyCreditCardExcludeCurrentCard={displayMyCreditCardExcludeCurrentCard}
              displayCurrentCreditCard={isChangeContractTerm}
              creditCardInfoForNextContract={creditCardInfoForNextContract}
              nextContractStartDate_YYYYMMDD={nextContractStartDate_YYYYMMDD}
            />
          </div>
        )
      })}
    </PIMStack>
  )
}

const isLastMonthInContractTerm = (nowContractEndDate: string): boolean => {
  const contractEndDate = dayjs(nowContractEndDate)
  const now = dayjs()

  const getYearOfContractEndDate = dayjs(contractEndDate).get('year')
  const nowYear = now.get('year')
  if (nowYear !== getYearOfContractEndDate) return true

  const getMonthOfContractEndDate = dayjs(contractEndDate).get('month')

  const nowMonth = now.get('month')

  return nowMonth !== getMonthOfContractEndDate
}

const getChangeTermForCarrier = (nowContractEndDate: string): string => {
  const contractEndDate = dayjs(nowContractEndDate)
  const startDate = dayjs(contractEndDate).startOf('month').format('YYYY/MM/DD')
  const endDate = dayjs(contractEndDate).endOf('month').format('YYYY/MM/DD')

  return `${startDate} 〜 ${endDate}`
}

type EditDisplayInputDetailPaymentMethodProps = JoinDisplayInputDetailPaymentMethodForProps & {
  displayMyCreditCardExcludeCurrentCard: boolean
  displayCurrentCreditCard: boolean
  creditCardInfoForNextContract: Nullable<MyCreditCardInfo>
} & Required<Pick<SelectCreditCardRadioListForProps, 'nextContractStartDate_YYYYMMDD'>>

const EditDisplayInputDetailPaymentMethod:
  React.VFC<EditDisplayInputDetailPaymentMethodProps> =
  ({
     paymentMethod,
     paymentInputDetailWithMethod,
     errorMessages,
     onChangeInputDetailInfo,
     onDeleteCardButtonClick,
     myCreditCardInfoList,
     displayMyCreditCardExcludeCurrentCard,
     displayCurrentCreditCard,
     creditCardInfoForNextContract,
     nextContractStartDate_YYYYMMDD,
   }) => {
    const handleCreditCardInputDetailInfoChange = useCallback(
      (changeInputDetailInfo: CreditCardInfo): void =>
        onChangeInputDetailInfo({
          ...paymentInputDetailWithMethod,
          creditCard: changeInputDetailInfo,
        }), [paymentInputDetailWithMethod],
    )

    const currentCreditCard = myCreditCardInfoList?.find(value => {
      if (creditCardInfoForNextContract === null) return undefined
      return value.cardId === creditCardInfoForNextContract.cardId
    })

    const myCreditCardInfoListExcludeCurrentCreditCard: Optional<UsingMyCreditCardInfo[]>
      = myCreditCardInfoList?.filter(
      (myCreditCardInfo) => {
        return (myCreditCardInfo.cardId !== currentCreditCard?.cardId)
      },
    )
    const getMyCreditCardInfoListExcludeCurrentCreditCard = (): UsingMyCreditCardInfo[] => {
      if (!myCreditCardInfoListExcludeCurrentCreditCard) return []

      if (displayMyCreditCardExcludeCurrentCard) {
        return myCreditCardInfoListExcludeCurrentCreditCard
      }
      return []
    }

    if (paymentInputDetailWithMethod.method !== paymentMethod) {
      return <></>
    }
    switch (paymentInputDetailWithMethod.method) {
      case 'credit_card':
        return <Spacer ml={'5%'}>
          <ErrorDisplay errors={errorMessages.creditCardTypeErrors}/>
          {
            !displayCurrentCreditCard && currentCreditCard && (
              <Spacer mt={'18px'}>
                <PIMStack spacing={'18px'} divider={<MBContentBodyDivider/>} showEndDivider>
                  <UnchangedCreditCardMethodItem
                    currentCreditCard={currentCreditCard}
                    creditCardInfo={paymentInputDetailWithMethod.creditCard}
                    nextContractStartDate_YYYYMMDD={nextContractStartDate_YYYYMMDD}
                    onChangeInputDetailInfo={(changeInputDetailInfo): void =>
                      handleCreditCardInputDetailInfoChange(changeInputDetailInfo)
                    }
                    errorMessages={errorMessages}
                  />
                </PIMStack>
              </Spacer>
            )
          }
          <Spacer mt={'18px'}>
            <SelectCreditCardRadioList
              myCreditCardInfoList={getMyCreditCardInfoListExcludeCurrentCreditCard()}
              creditCardInfo={paymentInputDetailWithMethod.creditCard}
              nextContractStartDate_YYYYMMDD={nextContractStartDate_YYYYMMDD}
              newCreditCardRegisterDisable={
                getMyCreditCardInfoListExcludeCurrentCreditCard().length >= 4
              }
              errorMessages={errorMessages}
              onChangeInputDetailInfo={(changeInputDetailInfo): void =>
                handleCreditCardInputDetailInfoChange(changeInputDetailInfo)
              }
              onDeleteCardButtonClick={onDeleteCardButtonClick}
            />
          </Spacer>
        </Spacer>
      case 'mobile_carrier':
        return <MobileCarrierInputComponent
          mobileCarrierInputInfo={paymentInputDetailWithMethod.mobileCarrier}
          errorMessage={errorMessages}
          onChangeInputDetailInfo={
            (value: MobileCarrierInfo): void =>
              onChangeInputDetailInfo({
                creditCard: paymentInputDetailWithMethod.creditCard,
                mobileCarrier: value,
                convenienceStore: paymentInputDetailWithMethod.convenienceStore,
              })
          }
        />
      case 'convenience_store':
        return <></>
      case 'none':
        return <></>
    }
  }

type UnchangedCreditCardMethodItemProps = {
  currentCreditCard: UsingMyCreditCardInfo
} & Pick<SelectCreditCardRadioListForProps,
  'creditCardInfo' |
  'nextContractStartDate_YYYYMMDD' |
  'onChangeInputDetailInfo' |
  'errorMessages'>
const UnchangedCreditCardMethodItem: React.VFC<UnchangedCreditCardMethodItemProps> = (props) => {
  const expired =
    isExpiredCreditCard(props.currentCreditCard.expireDate, props.nextContractStartDate_YYYYMMDD)
  const hasError =
    (props.errorMessages.errorMyCreditCardId === props.currentCreditCard.cardId)
    || (props.errorMessages.creditCardTypeErrors.length > 0)

  return (
    <div data-testid={'block-input-my-current-credit-card-information'}>
      <PIMStack spacing={1.5}>
        <PIMStack direction={'row'} alignItems={'baseline'} spacing={'6px'}>
          <MBMemberRadio
            id={'current-credit-card'}
            name="payment1"
            labelName={'クレジットカードを変更しない'}
            disabled={expired}
            hasError={expired ? false : hasError}
            isBoldLabel={true}
            fontRemSize={1.2}
            value={props.currentCreditCard.cardId}
            onChange={(e): void => {
              props.onChangeInputDetailInfo({
                creditCardType: 'myCard',
                myCreditCardInfo: {
                  selectedCardId: e.target.value,
                  securityCodeMap:
                  props.creditCardInfo.myCreditCardInfo.securityCodeMap,
                },
                newCreditCardInfo: props.creditCardInfo.newCreditCardInfo,
              })
            }}
            checked={isCheckedMyCardId(props.creditCardInfo, props.currentCreditCard)}
          />
          <PIMStack spacing={'2px'}>
            {expired && <div><ExpiredCardBadge/></div>}
            <MBInUseBadge/>
          </PIMStack>
        </PIMStack>

        {hasError && props.errorMessages.invalidCardErrors.length > 0 && (
          <Spacer ml={'28px'}>
            <PIMStack spacing={'6px'}>
              {props.errorMessages.invalidCardErrors.map((error, idx) => (
                <MBContentBodyItemWarningMessageTypography key={idx}>
                  {error}
                </MBContentBodyItemWarningMessageTypography>
              ))}
            </PIMStack>
          </Spacer>
        )}

        <CardInformationDiv disabled={expired}>
          <PIMStack direction={'row'} alignItems={'baseline'}>
            {props.currentCreditCard.cardNumber}
            <DisplayCreditCardExpireDate expireDate={props.currentCreditCard.expireDate}/>
          </PIMStack>
        </CardInformationDiv>
      </PIMStack>

      {
        props.creditCardInfo.creditCardType === 'myCard' &&
        props.currentCreditCard.cardId === props.creditCardInfo.myCreditCardInfo.selectedCardId
        && (
          <Spacer ml={'10%'}>
            <SecurityCodeInputForm
              securityCode={
                findSecurityCodeByCardId(
                  props.creditCardInfo.myCreditCardInfo.securityCodeMap,
                  props.currentCreditCard.cardId,
                )?.securityCode || ''
              }
              errorMessages={
                hasError
                  ? {
                    ...props.errorMessages,
                    creditCardSecurityCodeErrors: [],
                  }
                  : {
                    ...props.errorMessages,
                    invalidCardErrors: [],
                    creditCardSecurityCodeErrors: [],
                    myCreditCardSecurityCodeErrors: [],
                  }
              }
              onChangeInputSecurityCode={(value): void => {
                const _securityCodeMap: SecurityCodeForMyCreditCard[] =
                  Object.assign(props.creditCardInfo.myCreditCardInfo.securityCodeMap)
                const findCardIdInSecurityCodeMap =
                  findSecurityCodeByCardId(_securityCodeMap, props.currentCreditCard.cardId)
                if (findCardIdInSecurityCodeMap) {
                  findCardIdInSecurityCodeMap.securityCode = value
                }

                props.onChangeInputDetailInfo({
                  creditCardType: 'myCard',
                  myCreditCardInfo: {
                    selectedCardId: props.creditCardInfo.myCreditCardInfo.selectedCardId,
                    securityCodeMap: _securityCodeMap,
                  },
                  newCreditCardInfo: props.creditCardInfo.newCreditCardInfo,
                })
              }}
            />
          </Spacer>
        )
      }
    </div>
  )
}
